<!-- resources/js/views/Step.vue -->
<template>
    <div class="p-4 space-y-4">



        <TextEnigme :step="step" />

        <Medias :medias="step?.medias" />

        <!-- Completed non-final step -->
        <div v-if="isCompleted" class="space-y-4">

            <p class="italic text-gray-700" v-if="step.solution">Solution : {{ step.solution }}</p>
            <button
                @click="goNext"
                type="button"
                class="w-full py-2 bg-[#7b0f14] text-white rounded-2xl hover:bg-[#5c0c10] transition"
            >
                Étape suivante
            </button>
        </div>

        <!-- Active (not yet completed) step -->
        <div v-else-if="step?.input" class="space-y-4">

            <div
                class="mb-4 prose prose-indigo max-w-none"
                v-html="step?.input"
            ></div>

            <!-- Answer input & validate -->
            <input
                v-model="answer"
                type="text"
                placeholder="Votre réponse"
                class="w-full p-2 border rounded-lg"
                :hidden="step?.solution === null"
            />
            <button
                @click="checkAnswer"
                :hidden="step?.solution === null"
                class="w-full py-2 bg-[#7b0f14] text-white cursor-pointer rounded-lg hover:bg-[#5c0c10] transition"
            >
                Valider
            </button>

            <!-- Feedback message -->
            <p
                v-if="message"
                :class="messageClass"
                class="mt-4 text-center font-medium"
            >
                {{ message }}
            </p>
            <hr>
            <!-- Hint / Solution buttons -->
            <div class="flex gap-4 mb-4">
                <button
                    @click="showConfirm('hint')"
                    :disabled="hintVisible || solutionVisible"
                    :hidden="step?.hint === null"
                    class="flex-1 px-4 py-2 bg-yellow-200 text-yellow-800 rounded-l-xl hover:bg-yellow-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Afficher indice
                </button>
                <button
                    @click="showConfirm('solution')"
                    :disabled="solutionVisible"
                    :hidden="step?.solution === null"
                    class="flex-1 px-4 py-2 bg-red-200 text-red-800 rounded-r-xl hover:bg-red-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Afficher solution
                </button>
            </div>
            <p v-if="hintVisible" class="italic rounded-lg text-gray-600">
                Indice : {{ step.hint }}
            </p>
            <p v-if="solutionVisible" class="italic rounded-lg text-gray-700">
                Solution : {{ step.solution_info }}
            </p>
        </div>
        <div v-else class="text-gray-500 text-center">
            <button
                @click="goNext"
                type="button"
                v-if="step && !step?.data?.end"
                class="w-full py-2 bg-[#7b0f14] text-white rounded-2xl hover:bg-[#5c0c10] transition"
            >
                Étape suivante
            </button>
            <p
                v-else-if="message"
                :class="messageClass"
                class="font-medium"
            >
                {{ message }}
            </p>
        </div>
    </div>

    <!-- Modal de confirmation -->
    <transition name="fade">
        <div
            v-if="confirmType"
            class="fixed inset-0 flex items-center justify-center bg-black/80 z-50"
        >
            <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-sm p-6">
                <h3 class="text-lg font-semibold mb-4">
                    Confirmation
                </h3>
                <p class="mb-6">
                    Vous allez utiliser
                    <strong>{{ confirmType === 'hint' ? 'un indice' : 'la solution' }}</strong>
                    et perdre
                    <strong>{{ confirmType === 'hint' ? hintCost : solutionCost }} points</strong>.
                    Continuer ?
                </p>
                <div class="flex justify-end gap-4">
                    <button
                        @click="cancelConfirm"
                        class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                    >
                        Annuler
                    </button>
                    <button
                        @click="doConfirm"
                        class="px-4 py-2 bg-[#7b0f14] text-white rounded hover:bg-[#5c0c10] transition"
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { useScoreStore } from '@/stores/score'
import Score from '@/components/Score.vue'
import TextEnigme from '../components/TextEnigme.vue';
import Medias from '../components/Medias.vue';

const route = useRoute()
const router = useRouter()
const scoreStore = useScoreStore()

const step = ref(null)
const steps = ref([])           // ← on stocke la liste complète
const answer = ref('')
const message = ref('')
const messageClass = ref('')
const hintVisible = ref(false)
const solutionVisible = ref(false)
const hintCost = ref(0)
const solutionCost = ref(0)
const wrongAnswercost = ref(0)
const isDisabled = ref(true)

// type de confirmation ('hint' | 'solution' | null)
const confirmType = ref(null)

// reactive score
const score = computed(() => scoreStore.score)

const emit = defineEmits({
    'set-blink-map': (value) => typeof value === 'boolean'
})

// track unlocked steps
const unlockedStep = ref(
    parseInt(localStorage.getItem('unlockedStep') || '0', 10)
)

function extractSteps(data) {
    if (Array.isArray(data)) {
        return data
    }

    if (Array.isArray(data?.data)) {
        return data.data
    }

    if (Array.isArray(data?.steps)) {
        return data.steps
    }

    return []
}

function unlockStep(id) {
    const numericId = Number(id)

    if (!Number.isFinite(numericId)) {
        return
    }

    const nextUnlockedStep = Math.max(unlockedStep.value || 0, numericId)

    unlockedStep.value = nextUnlockedStep
    localStorage.setItem('unlockedStep', String(nextUnlockedStep))
}

// --- MODIF LOADSTEP : utilise /api/steps en cache ---
async function loadSteps() {
    if (steps.value.length) {
        return
    }

    const res = await axios.get('/api/steps')
    steps.value = extractSteps(res.data)
}

// determine if current step is completed
const isCompleted = computed(
    () => step.value && unlockedStep.value > step.value.id
)

// load a step from API
async function loadStep(id) {
    // 1️⃣ Charge la liste si pas déjà fait
    try {
        await loadSteps()
    } catch (error) {
        console.error('Impossible de charger les étapes.', error)
        step.value = null
        message.value = '⚠️ Impossible de charger les étapes.'
        messageClass.value = 'text-yellow-600'
        return
    }

    // 2️⃣ Recherche l'étape dans le tableau
    const num = parseInt(id, 10)
    const found = steps.value.find(s => Number(s.id) === num)

    if (!found) {
        step.value = null
        message.value = '⚠️ Étape introuvable.'
        messageClass.value = 'text-yellow-600'
        return
    }

    // 3️⃣ Initialisation de l'état local pour cette étape
    step.value = found
    answer.value = ''
    message.value = ''
    messageClass.value = ''
    hintVisible.value = false
    solutionVisible.value = false

    // 4️⃣ Applique les coûts dynamiques depuis found.data ou defaults
    hintCost.value     = found.data?.hint_cost     ?? scoreStore.hintCost
    solutionCost.value = found.data?.solution_cost ?? scoreStore.solutionCost
    //wrongAnswercost.value = found.data?.wrong_answer_cost ?? scoreStore.wrongAnswerCost

    // 5️⃣ Restaure l'état hint/solution depuis le localStorage
    const hintKey = `step_${id}_hint`
    const solKey  = `step_${id}_solution`
    hintVisible.value     = JSON.parse(localStorage.getItem(hintKey) || 'false')
    solutionVisible.value = JSON.parse(localStorage.getItem(solKey) || 'false')

    // 6️⃣ Démarre le timer si c'est la première étape
    if (id === '1' && !scoreStore.started) {
        scoreStore.initDefault(
            found.data?.score           ?? scoreStore.defaultScore,
            hintCost.value,
            solutionCost.value,
            wrongAnswercost.value
        )
        scoreStore.startTimer()
    }

    // 7️⃣ Si c’est la dernière étape, arrête le timer
    if (found.data?.end) {
        scoreStore.stopTimer()
    }

    //si l'étape n'as pas de solution, on désactive le bouton de validation et on le réactive dans 20 secondes
    isDisabled.value = !found.solution
}


// chargement initial et rechargement au changement d’URL
onMounted(() => loadStep(route.params.id))
watch(() => route.params.id, newId => loadStep(newId))
watch(
    () => route.params.id,
    () => {
        unlockedStep.value = parseInt(
            localStorage.getItem('unlockedStep') || '0',
            10
        )
    }
)

// navigate to next step
function goNext() {
    if (!step.value) {
        message.value = '⚠️ Étape introuvable.'
        messageClass.value = 'text-yellow-600'
        return
    }

    const nextId = Number(step.value.id) + 1

    if (!Number.isFinite(nextId)) {
        message.value = '⚠️ Étape introuvable.'
        messageClass.value = 'text-yellow-600'
        return
    }

    unlockStep(nextId)

    //check if step.value.data.map exists
    console.log(step.value)
    if(step.value.data?.map && step.value.data.map) {
        console.log(`Débloque la map pour l'étape ${step.value.id} : ${step.value.data.map}`)
        // si l'étape a une map, on la débloque
        localStorage.setItem('unlockedMap', step.value.data.map)
        //on fait blinker l'icone map de la navbar
        emit('set-blink-map', true)
    }
    console.log(`Charge page /step/${nextId}`)
    router.push(`/step/${nextId}`)
}

// Confirmation popups
// fonction appelée au clic
function showConfirm(type) {
    confirmType.value = type
}

// annulation
function cancelConfirm() {
    confirmType.value = null
}

// exécution après confirmation
function doConfirm() {
    if (!step.value) {
        confirmType.value = null
        return
    }

    if (confirmType.value === 'hint') {
        hintVisible.value = true
        localStorage.setItem(`step_${step.value.id}_hint`, 'true')
        scoreStore.useHint()
    } else if (confirmType.value === 'solution') {
        solutionVisible.value = true
        localStorage.setItem(`step_${step.value.id}_solution`, 'true')
        scoreStore.useSolution()
    }
    confirmType.value = null
}

// reveal hint
function revealHint() {
    if (!hintVisible.value && !solutionVisible.value) {
        hintVisible.value = true
        localStorage.setItem(
            `step_${step.value.id}_hint`,
            'true'
        )
        scoreStore.useHint(hintCost.value)
    }
}

// reveal solution
function revealSolution() {
    if (!solutionVisible.value) {
        solutionVisible.value = true
        localStorage.setItem(
            `step_${step.value.id}_solution`,
            'true'
        )
        scoreStore.useSolution(solutionCost.value)
    }
}

// check answer and move on or finish
// helper pour normaliser string (minuscules + sans accents)
function normalizeAnswer(str) {
    return String(str ?? '')
        .normalize('NFD')                 // décompose les caractères accentués
        .replace(/\p{Diacritic}/gu, '')   // supprime tous les diacritiques
        .toLowerCase()                    // en minuscules
        .trim()
}

async function checkAnswer() {
    if (!step.value || !step.value.solution) return

    const userAns = normalizeAnswer(answer.value)
    const solAns  = normalizeAnswer(step.value.solution)
    const isCorrect = solAns.includes('|')
        ? solAns.split('|').some(answer => userAns === normalizeAnswer(answer.trim()))
        : userAns === solAns
    if (isCorrect) {
        message.value      = '✔️ Correct ! Étape suivante…'
        messageClass.value = 'text-green-600'
        console.log(step.value)

        const nextId = Number(step.value.id) + 1
        unlockStep(nextId)

        // stop timer si fin
        if (step.value?.data?.end) {
            scoreStore.stopTimer()
        }

        setTimeout(() => goNext(), 1500)
    } else {
        scoreStore.wrongAnswer(wrongAnswercost.value)
        message.value      = '❌ Réponse incorrecte, réessayez.'
        messageClass.value = 'text-red-600'
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .2s;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>
